import { ArrowRight, Bell, Mail, MessageCircle, Smartphone } from "lucide-react";
import { Link } from "react-router";
import { PageSection } from "nazare-ui/components/page-section";

import { useNotificationTypesConfig } from "../../../notifications-admin-api";
import { SkeletonBlock } from "../../../skeleton-block";
import { useDocumentTitle } from "../../../use-document-title";
import styles from "./landing.module.css";

/**
 * `/notifications` — overview landing page: what notification types exist
 * and whether each delivery channel is actually configured on the
 * infrastructure side (SES/Slack bot token/WhatsApp API — real status from
 * the BFF's `/notification-types` config route, not guessed), plus a way
 * in to the per-user matrix at `/notifications/users` (moved there from
 * this path — see router.tsx).
 *
 * Deliberately does NOT import anything from `./index.tsx` (the matrix
 * page) or edit `../../../notifications-admin-api.ts` beyond using its
 * already-existing `useNotificationTypesConfig` export — both files were
 * being actively edited by a parallel workflow stream when this page was
 * built, so this stays a self-contained addition to avoid clobbering that
 * work. `NotificationTypeConfig`'s declared type doesn't include the
 * `channels` field the route actually returns (confirmed live) — cast
 * locally rather than widening the shared type out from under that
 * in-flight work.
 */

type ChannelStatus = { status: string };
type NotificationTypeWithChannels = {
  type: string;
  label: string;
  description: string;
  channels: {
    email: ChannelStatus;
    whatsapp: ChannelStatus;
    slack: ChannelStatus;
    push: ChannelStatus;
  };
};

const CHANNEL_META = [
  { key: "email" as const, label: "Email", Icon: Mail },
  { key: "whatsapp" as const, label: "WhatsApp", Icon: MessageCircle },
  { key: "slack" as const, label: "Slack", Icon: Bell },
  { key: "push" as const, label: "Push", Icon: Smartphone },
];

function isActive(status: string): boolean {
  return "active" === status;
}

export function NotificationsLandingPage() {
  useDocumentTitle("Notifications");

  const { data: types, isLoading } = useNotificationTypesConfig();
  const typesWithChannels = (types ?? []) as unknown as NotificationTypeWithChannels[];

  // A type's overall channel-configuration status (used for the summary
  // strip at the top) — how many of its channels are actually wired up on
  // the infra side, out of how many it could use.
  const configuredCount = typesWithChannels.reduce(
    (count, type) => count + CHANNEL_META.filter((c) => isActive(type.channels[c.key]?.status ?? "")).length,
    0,
  );
  const totalChannelSlots = typesWithChannels.length * CHANNEL_META.length;

  return (
    <div className={styles.page}>
      <Link to="/notifications/users" className={styles.manageCard}>
        <span className={styles.manageCardText}>
          <span className={styles.manageCardTitle}>Manage users</span>
          <span className={styles.manageCardSubtitle}>
            Search any user and bulk-edit their notification settings
          </span>
        </span>
        <ArrowRight size={18} aria-hidden="true" />
      </Link>

      <PageSection
        title="Notification types"
        count={isLoading ? undefined : typesWithChannels.length}
        description={isLoading ? undefined : `${configuredCount} of ${totalChannelSlots} channel slots configured`}
        padded
      >
        {isLoading ? (
          <div className={styles.skeletonStack} aria-hidden="true">
            {Array.from({ length: 6 }, (_, index) => (
              <SkeletonBlock key={index} height={48} />
            ))}
          </div>
        ) : (
          <>
            <ul className={styles.typeList}>
              {typesWithChannels.map((type) => (
                <li key={type.type} className={styles.typeRow}>
                  <div className={styles.typeMeta}>
                    <span className={styles.typeLabel}>{type.label}</span>
                    <span className={styles.typeDescription}>{type.description}</span>
                  </div>
                  <div className={styles.channelStatusRow}>
                    {CHANNEL_META.map(({ key, label, Icon }) => {
                      const active = isActive(type.channels[key]?.status ?? "");
                      return (
                        <span
                          key={key}
                          className={active ? styles.channelActive : styles.channelInactive}
                          title={`${label}: ${active ? "configured" : "not configured"}`}
                        >
                          <Icon size={13} aria-hidden="true" />
                        </span>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </PageSection>
    </div>
  );
}
